'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { getAccount } from '@/lib/auth'
// Billing and Compliance moved to /subscription

export default function SettingsPage() {
  const router = useRouter()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [teams, setTeams] = useState([])
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const [teamInitError, setTeamInitError] = useState('')
  const [twilioCredentials, setTwilioCredentials] = useState({
    accountSid: '',
    authToken: '',
    phoneNumber: ''
  })
  const [cleancloudCredentials, setCleancloudCredentials] = useState({
    businessName: '',
    apiKey: ''
  })
  const [csvFile, setCsvFile] = useState(null)
  const [csvUploading, setCsvUploading] = useState(false)
  const [csvResult, setCsvResult] = useState(null)

  // Billing moved to /subscription

  // Compliance moved to /subscription

  // submitComplianceTf removed (now in /subscription)

  // ensureTenant removed (now in /subscription)

  // startSubscription removed (now in /subscription)

  useEffect(() => {
    checkAuth()
    initTeamsAndSettings()
  }, [])

  async function initTeamsAndSettings() {
    try {
      // Ensure there is at least one Team and resolve default when only one
      const res = await fetch('/api/teams/resolve', { method: 'GET', credentials: 'include', cache: 'no-store' })
      if (res.status === 409) {
        // Multiple teams: fetch list for selector
        const listRes = await fetch('/api/teams/resolve?list=1', { method: 'GET', credentials: 'include', cache: 'no-store' })
        if (listRes.ok) {
          const data = await listRes.json()
          const arr = Array.isArray(data?.teams) ? data.teams : []
          setTeams(arr)
          // Do not auto-select; wait for user selection
        } else {
          setTeamInitError('Unable to load your teams. Please try again.')
        }
      } else if (res.ok) {
        const data = await res.json()
        if (data?.teamId) setSelectedTeamId(data.teamId)
      } else {
        setTeamInitError('Failed to resolve team. Please try again.')
      }
    } catch (e) {
      setTeamInitError('Failed to resolve team.')
    } finally {
      // Load settings after we know selectedTeamId (or if there is none, endpoints will auto-resolve when only one team exists)
      loadTwilioCredentials()
      loadCleancloudCredentials()
    }
  }


  async function handleCsvUpload(e) {
    e.preventDefault()
    setMessage('')
    setCsvResult(null)
    if (!csvFile) {
      setMessage('Error: Please choose a CSV file to upload.')
      return
    }
    try {
      setCsvUploading(true)
      const fd = new FormData()
      fd.append('file', csvFile)
      if (selectedTeamId) fd.append('teamId', selectedTeamId)
      const res = await fetch('/api/upload/csv', {
        method: 'POST',
        body: fd,
        credentials: 'include'
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) {
        setMessage(`Error: ${data?.error || 'CSV upload failed'}`)
      } else {
        setCsvResult(data)
        setMessage('CSV uploaded successfully.')
      }
    } catch (err) {
      setMessage(`Error: ${err?.message || String(err)}`)
    } finally {
      setCsvUploading(false)
    }
  }

  async function checkAuth() {
    try {
      const account = await getAccount()
      if (!account) {
        router.replace('/login')
        return
      }
      // Subscription guard
      try {
        const res = await fetch('/api/billing/me', { method: 'GET', credentials: 'include', cache: 'no-store' })
        if (res.ok) {
          const data = await res.json()
          const status = (data?.stripe?.status || '').toLowerCase()
          const subscribed = ['active','trialing','past_due'].includes(status)
          if (!subscribed) {
            router.replace('/subscription')
            return
          }
        } else {
          router.replace('/subscription')
          return
        }
      } catch {
        router.replace('/subscription')
        return
      }
      setUser(account)
    } catch (error) {
      router.replace('/login')
    } finally {
      setLoading(false)
    }
  }

  async function loadTwilioCredentials() {
    try {
      const response = await fetch('/api/settings/twilio', {
        method: 'GET',
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setTwilioCredentials({
          accountSid: data.accountSid || '',
          authToken: data.authToken || '',
          phoneNumber: data.phoneNumber || ''
        })
      }
    } catch (error) {
      console.error('Error loading Twilio credentials:', error)
    }
  }

  async function loadCleancloudCredentials() {
    try {
      const url = selectedTeamId ? `/api/settings/integrations/cleancloud?teamId=${encodeURIComponent(selectedTeamId)}` : '/api/settings/integrations/cleancloud'
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setCleancloudCredentials({
          businessName: data.businessName || '',
          apiKey: data.apiKey || ''
        })
      }
    } catch (error) {
      console.error('Error loading CleanCloud credentials:', error)
    }
  }

  async function handleTwilioSave(e) {
    e.preventDefault()
    setSaving(true)
    setMessage('')

    try {
      const response = await fetch('/api/settings/twilio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(twilioCredentials)
      })

      if (response.ok) {
        setMessage('Twilio credentials saved successfully!')
      } else {
        const error = await response.json()
        setMessage(`Error: ${error.message || 'Failed to save Twilio credentials'}`)
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleCleancloudSave(e) {
    e.preventDefault()
    setSaving(true)
    setMessage('')

    try {
      const url = selectedTeamId ? `/api/settings/integrations/cleancloud?teamId=${encodeURIComponent(selectedTeamId)}` : '/api/settings/integrations/cleancloud'
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(cleancloudCredentials)
      })

      if (response.ok) {
        setMessage('CleanCloud credentials saved successfully!')
      } else {
        const error = await response.json()
        setMessage(`Error: ${error.message || 'Failed to save CleanCloud credentials'}`)
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
        <p className="text-slate-400">Manage your Twilio and CleanCloud settings.</p>
        <div className="mt-3 text-sm bg-slate-900/50 border border-slate-700 text-slate-300 rounded-md p-3">
          Billing and Toll‑Free compliance have moved to the <a href="/subscription" className="text-purple-400 hover:text-purple-300 underline">Subscription</a> page.
        </div>
        {/* Team selector (only when multiple teams) */}
        {teamInitError && (
          <div className="mt-3 text-sm bg-red-900/50 border border-red-700 text-red-300 rounded-md p-3">{teamInitError}</div>
        )}
        {Array.isArray(teams) && teams.length > 1 && (
          <div className="mt-4 flex items-center gap-3">
            <label className="text-slate-300 text-sm">Active Team</label>
            <select
              className="bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 py-2"
              value={selectedTeamId}
              onChange={(e) => {
                setSelectedTeamId(e.target.value)
                // Refresh CleanCloud credentials for newly selected team
                loadCleancloudCredentials()
              }}
            >
              <option value="">Select a team…</option>
              {teams.map((t) => {
                const tid = t?.$id || t?.teamId || t?.team?.$id || t?.teamId
                const name = t?.name || t?.team?.name || 'Team'
                return <option key={tid} value={tid}>{name}</option>
              })}
            </select>
          </div>
        )}
      </div>

      {message && (
        <div className={`mb-6 p-3 rounded-md text-sm ${
          message.includes('Error') || message.includes('failed') 
            ? 'bg-red-900/50 text-red-300 border border-red-700' 
            : 'bg-green-900/50 text-green-300 border border-green-700'
        }`}>
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Billing moved to Subscription page */}
        {/* See /subscription */}
        {/* Compliance moved to Subscription page */}

        {/* Twilio Configuration */}
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">Twilio Configuration</h2>
            <p className="text-slate-400 text-sm">
              Configure your Twilio credentials to enable SMS sending. Your credentials are encrypted and stored securely.
            </p>
          </div>

          <form onSubmit={handleTwilioSave} className="space-y-4">
            <div>
              <label htmlFor="accountSid" className="block text-sm font-medium text-slate-300 mb-2">
                Account SID
              </label>
              <input
                type="text"
                id="accountSid"
                value={twilioCredentials.accountSid}
                onChange={(e) => setTwilioCredentials(prev => ({ ...prev, accountSid: e.target.value }))}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="authToken" className="block text-sm font-medium text-slate-300 mb-2">
                Auth Token
              </label>
              <input
                type="password"
                id="authToken"
                value={twilioCredentials.authToken}
                onChange={(e) => setTwilioCredentials(prev => ({ ...prev, authToken: e.target.value }))}
                placeholder="••••••••••••••••••••••••••••••••"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-300 mb-2">
                Twilio Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={twilioCredentials.phoneNumber}
                onChange={(e) => setTwilioCredentials(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="+1234567890"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                {saving ? 'Saving...' : 'Save Twilio Credentials'}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-700">
            <h3 className="text-lg font-medium text-white mb-2">How to get your Twilio credentials:</h3>
            <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
              <li>Sign up for a Twilio account at <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">twilio.com</a></li>
              <li>Go to your Twilio Console Dashboard</li>
              <li>Find your Account SID and Auth Token in the "Account Info" section</li>
              <li>Purchase a phone number from the Phone Numbers section</li>
              <li>Copy and paste the credentials above</li>
            </ol>
          </div>
        </div>

        {/* CleanCloud Configuration */}
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">CleanCloud Configuration</h2>
            <p className="text-slate-400 text-sm">
              Configure your CleanCloud credentials to integrate with your laundry management system. Your credentials are encrypted and stored securely.
            </p>
          </div>

          <form onSubmit={handleCleancloudSave} className="space-y-4">
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-slate-300 mb-2">
                Business Name
              </label>
              <input
                type="text"
                id="businessName"
                value={cleancloudCredentials.businessName}
                onChange={(e) => setCleancloudCredentials(prev => ({ ...prev, businessName: e.target.value }))}
                placeholder="Your Business Name"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="apiKey" className="block text-sm font-medium text-slate-300 mb-2">
                API Key
              </label>
              <input
                type="password"
                id="apiKey"
                value={cleancloudCredentials.apiKey}
                onChange={(e) => setCleancloudCredentials(prev => ({ ...prev, apiKey: e.target.value }))}
                placeholder="••••••••••••••••••••••••••••••••"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                {saving ? 'Saving...' : 'Save CleanCloud Credentials'}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-700">
            <h3 className="text-lg font-medium text-white mb-2">How to get your CleanCloud credentials:</h3>
            <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
              <li>Log into your CleanCloud admin dashboard</li>
              <li>Navigate to Settings → API Settings</li>
              <li>Generate or copy your API key</li>
              <li>Use your business name as registered in CleanCloud</li>
              <li>Copy and paste the credentials above</li>
            </ol>
          </div>
        </div>

        {/* CSV Import (CleanCloud exports) */}
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">CSV Import</h2>
            <p className="text-slate-400 text-sm">
              Upload a CleanCloud customer CSV to seed your tenant’s data immediately. The daily sync will reconcile and attach CleanCloud IDs.
            </p>
          </div>

          <form onSubmit={handleCsvUpload} className="space-y-4">
            <div>
              <label htmlFor="csvFile" className="block text-sm font-medium text-slate-300 mb-2">
                CSV File
              </label>
              <input
                type="file"
                id="csvFile"
                accept=".csv,text/csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-700 file:text-white hover:file:bg-slate-600"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={csvUploading || !csvFile}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                {csvUploading ? 'Uploading…' : 'Upload CSV'}
              </button>
            </div>
          </form>

          {csvResult && (
            <div className="mt-4 text-sm text-slate-300 bg-slate-900/50 border border-slate-700 rounded-md p-3">
              <div>Batch: <span className="text-slate-200 font-mono">{csvResult.importBatchId}</span></div>
              <div>Total rows: {csvResult.totalRows}</div>
              <div>Parsed: {csvResult.parsed} • Deduped: {csvResult.deduped} • Written: {csvResult.written}</div>
              {Array.isArray(csvResult.errors) && csvResult.errors.length > 0 && (
                <div className="mt-2 text-red-300">Errors: {csvResult.errors.length} (see console for details)</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
